import { clientsContractsListOptions, ReturnContractReadable } from '@crm/api'
import { ContractSignedStatus } from '@crm/components'
import { useFetchSiAsig } from '@crm/components/InfoTabs/Tabs/ContractsCol/useFetchSiAsig'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import {
  DateTimeCell,
  DayLabel,
  TableActions,
  TableContainer
} from '@shared/components'
import { useQueryParams } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { useQuery } from '@tanstack/react-query'
import { TableColumnsType, Typography } from 'antd'
import { MenuProps } from 'antd/lib'
import { Refresh } from 'iconsax-react'
import { useParams } from 'react-router-dom'

export const ClientContractsCol = () => {
  const { t } = useLocales()
  const { id } = useParams()
  const params = useQueryParams()
  const navigate = useAppNavigate()

  const { mutate: fetchSiAsig, isPending: isFetchingSiAsig } = useFetchSiAsig()

  const actionItems = (
    contract: ReturnContractReadable
  ): MenuProps['items'] => [
    {
      label: t('contracts.fetch_si_asig'),
      key: 'fetch-si-asig',
      icon: <Refresh size={20} />,
      disabled: isFetchingSiAsig,
      onClick: () => contract.id && fetchSiAsig(contract.id)
    }
  ]

  const { data, isLoading } = useQuery({
    ...clientsContractsListOptions({
      path: { client_id: id! },
      query: { ...params }
    }),
    enabled: !!id
  })

  const columns: TableColumnsType<ReturnContractReadable> = [
    {
      title: t('contracts.contract_number'),
      dataIndex: 'number',
      key: 'number'
    },
    {
      title: t('contracts.value'),
      dataIndex: 'value',
      key: 'value'
    },
    {
      title: t('contracts.currency'),
      dataIndex: ['currency', 'name'],
      key: 'currency_name'
    },
    {
      title: t('task.owner'),
      dataIndex: ['owner', 'name'],
      key: 'owner_name'
    },
    {
      title: t('contracts.opportunity'),
      dataIndex: ['opportunity', 'id'],
      key: 'opportunity_name',
      render: (id, record) =>
        id ? (
          <Typography.Link
            onClick={() =>
              navigate(
                paths.opportunitiesDetails.root(
                  String(id),
                  String(
                    record?.opportunity?.lead || record?.opportunity?.client
                  )
                )
              )
            }
          >
            {id}
          </Typography.Link>
        ) : (
          '-'
        )
    },
    {
      title: t('contracts.product'),
      dataIndex: ['product', 'name'],
      key: 'products_name'
    },
    {
      title: t('contracts.registration_number'),
      dataIndex: ['vehicle', 'registration_number'],
      key: 'registration_number',
      render: (registrationNumber) => registrationNumber || '-'
    },
    {
      title: t('contracts.vehicle'),
      key: 'vehicle',
      render: (_, { vehicle }) =>
        [vehicle?.brand, vehicle?.model].filter(Boolean).join(' / ') || '-'
    },
    {
      title: t('contracts.valid_from'),
      dataIndex: 'valid_from',
      key: 'valid_from',
      render: (validFrom) => <DayLabel date={validFrom} />
    },
    {
      title: t('contracts.valid_to'),
      dataIndex: 'valid_to',
      key: 'valid_to',
      render: (validTo) => <DayLabel date={validTo} />
    },
    {
      title: t('contracts.status'),
      key: 'status',
      render: (_, contract) => <ContractSignedStatus contract={contract} />
    },
    {
      title: t('contracts.signed_at'),
      dataIndex: 'signed_at',
      key: 'signed_at',
      render: (signedAt) => <DayLabel date={signedAt} />
    },
    {
      title: t('contracts.synced_at'),
      dataIndex: 'synced_at',
      key: 'synced_at',
      render: (syncedAt) => <DateTimeCell date={syncedAt} />
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      render: (_, contract) => <TableActions options={actionItems(contract)} />
    }
  ]

  return (
    <TableContainer
      rowKey="id"
      columns={columns}
      data={data}
      loading={isLoading}
    />
  )
}
